/**
 * createSharedSettingsStore — a reusable SharePoint-backed store for a module's
 * user-customizable style settings, so the owner configures once and every user
 * sees it (instead of the per-browser localStorage the modules used).
 *
 * Same mechanism as GridSettingsService/HomeSettingsService: one JSON row in a
 * brand-new, single-purpose SharePoint list this store alone owns and
 * auto-creates on first save.
 *
 * READS use a SILENT token (never redirects) and are best-effort — any failure
 * returns null so the module keeps its local/default look. WRITES use the
 * interactive token (owner clicking Save on a settings page).
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { getSharePointTokenSilent } from "../../shell/auth/tokens";

// Same site the Production Grid uses (iSyn_GridSettings) — proven to sync
// across all users. NOT the env default (VITE_SHAREPOINT_SITE_URL → CRM).
const SITE_URL = MFG_SITE_URL.replace(/\/$/, "");
const FIELD = "settingsJson";

interface Row {
  Id: number;
  settingsJson?: string | null;
}

export interface SharedSettingsStore<T> {
  /** Last shared value cached locally — instant first paint, no flash. */
  getCached(): Partial<T> | null;
  /** Silent read of the shared value; null if unavailable/none configured. */
  getShared(): Promise<Partial<T> | null>;
  /** Write the shared value (owner action, interactive token). */
  saveShared(value: T): Promise<void>;
  /** Earlier saved values of the shared row, newest first (needs list versioning; empty when it was off or unreadable). */
  getVersions(): Promise<Array<{ label: string; at: string; value: Partial<T> }>>;
}

/**
 * @param listName A brand-new list title this store alone owns (e.g. "iSyn_AcctSettings").
 * @param strip Optional transform to drop large/local-only fields before storing.
 */
export function createSharedSettingsStore<T>(
  listName: string,
  strip?: (value: T) => Partial<T>,
): SharedSettingsStore<T> {
  const sp = createSharePointClient(SITE_URL);
  const cacheKey = `ish:shared:${listName}`;
  let entityType: string | null = null;

  const payload = (value: T) => JSON.stringify(strip ? strip(value) : value);

  async function ensureList(): Promise<void> {
    try {
      await sp.getListItems(listName, { select: "Id", top: 1 });
      return;
    } catch (err) {
      const msg = (err as Error).message ?? "";
      if (!/404|does not exist|Invalid List Name/i.test(msg)) throw err;
    }
    await sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: listName,
        Description: "i-SYNERGY — shared module style settings (single JSON row). Auto-created on first Save.",
        BaseTemplate: 100,
      }),
    });
    await sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')/fields`, {
      method: "POST",
      body: JSON.stringify({ __metadata: { type: "SP.Field" }, Title: FIELD, FieldTypeKind: 3, Required: false }),
    });
  }

  let versioningAsked = false;
  /** Keep every saved version of the row, so an overwrite can be undone. Best effort: needs permission to manage the list. */
  async function ensureVersioning(): Promise<void> {
    if (versioningAsked) return; versioningAsked = true;
    try {
      await sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')`, {
        method: "POST",
        headers: { "If-Match": "*", "X-HTTP-Method": "MERGE" },
        body: JSON.stringify({ __metadata: { type: "SP.List" }, EnableVersioning: true }),
      });
    } catch { /* not allowed to change list settings — saves still work, just without history */ }
  }

  async function getEntityType(): Promise<string> {
    if (entityType) return entityType;
    const res = await sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')?$select=ListItemEntityTypeFullName`,
    );
    entityType = res.d.ListItemEntityTypeFullName;
    return entityType;
  }

  return {
    getCached() {
      try {
        const raw = localStorage.getItem(cacheKey);
        if (raw) return JSON.parse(raw) as Partial<T>;
      } catch {
        /* ignore */
      }
      return null;
    },

    async getShared() {
      const token = await getSharePointTokenSilent();
      if (!token) return null;
      try {
        const url = `${SITE_URL}/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')/items?$select=Id,${FIELD}&$top=1`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}`, Accept: "application/json;odata=verbose" } });
        if (!res.ok) return null;
        const json = (await res.json()) as { d: { results: Row[] } };
        const raw = json.d.results[0]?.settingsJson;
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<T>;
        try { localStorage.setItem(cacheKey, JSON.stringify(parsed)); } catch { /* ignore */ }
        return parsed;
      } catch {
        return null;
      }
    },

    async getVersions() {
      const token = await getSharePointTokenSilent();
      if (!token) return [];
      try {
        const base = `${SITE_URL}/_api/web/lists/getbytitle('${encodeURIComponent(listName)}')`;
        const h = { Authorization: `Bearer ${token}`, Accept: "application/json;odata=verbose" };
        const it = await fetch(`${base}/items?$select=Id&$top=1`, { headers: h });
        if (!it.ok) return [];
        const id = ((await it.json()) as { d: { results: Row[] } }).d.results[0]?.Id;
        if (!id) return [];
        const res = await fetch(`${base}/items(${id})/versions`, { headers: h });
        if (!res.ok) return [];
        const json = (await res.json()) as { d: { results: Array<{ VersionLabel?: string; Created?: string; settingsJson?: string | null }> } };
        const out: Array<{ label: string; at: string; value: Partial<T> }> = [];
        for (const v of json.d.results) {
          if (!v.settingsJson) continue;
          try { out.push({ label: v.VersionLabel ?? "", at: v.Created ?? "", value: JSON.parse(v.settingsJson) as Partial<T> }); } catch { /* skip an unreadable version */ }
        }
        return out.sort((a, b) => b.at.localeCompare(a.at));
      } catch { return []; }
    },

    async saveShared(value: T) {
      await ensureList();
      void ensureVersioning();
      const et = await getEntityType();
      const existing = await sp.getListItems<Row>(listName, { select: "Id", top: 1 });
      const body = { __metadata: { type: et }, [FIELD]: payload(value) };
      if (existing[0]) await sp.updateItem(listName, existing[0].Id, body);
      else await sp.createItem(listName, body);
      try { localStorage.setItem(cacheKey, payload(value)); } catch { /* ignore */ }
    },
  };
}
